// React entry point.
//
// Order matters here:
//   1. Install the harmonyShim BEFORE React renders. Tool components call
//      `window.harmony.api.getUserName()` synchronously inside their first
//      effect — if the global is missing, they fall back to "unknown" and
//      we end up with garbage run IDs.
//   2. Apply the saved theme so the page paints in the right mode (no
//      flash of light theme on a dark-mode user's load).
//   3. Validate config — fail loud if Cognito vars are missing in
//      production mode.
//   4. Mount the React tree.

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import { installHarmonyShim } from './lib/harmonyShim.js';
import { assertConfigValid } from './config.js';
import './index.css';

// 1. Install the global shim. setHarmonyUsername() will be called by the
//    AuthProvider once auth resolves. Until then `getUserName()` returns
//    "unknown" — which is fine because every tool's first call is wrapped
//    in a retry loop that picks up the real value on a later tick.
installHarmonyShim();

// 2. Apply theme before first paint. Same trick the Harmony Home.jsx uses.
(function applyStoredTheme() {
  const stored = localStorage.getItem('smartbiz-theme');
  const isDark = stored
    ? stored === 'dark'
    : window.matchMedia('(prefers-color-scheme: dark)').matches;
  document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  if (isDark) document.body.classList.add('awsui-dark-mode');
})();

// 3. Surface config errors immediately rather than at first auth attempt.
try {
  assertConfigValid();
} catch (err) {
  // Render the error in-page so it's visible without DevTools open.
  document.body.innerHTML = `
    <div style="font-family: system-ui; padding: 40px; max-width: 720px; margin: 0 auto;">
      <h2 style="color: #b91c1c;">Configuration error</h2>
      <pre style="background: #fef2f2; padding: 16px; border-radius: 6px; white-space: pre-wrap;">${err.message}</pre>
      <p>See <code>.env.example</code> in the project root.</p>
    </div>
  `;
  throw err;
}

// 4. Mount.
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
